import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, DollarSign, ArrowLeftRight, Calculator, Star, TrendingUp, Plus, Trash2, RefreshCw } from "lucide-react";
import SyrianFlag from "@/components/SyrianFlag";
import { CURRENCY_CATALOG, CURRENCY_MAP, currencyName } from "@/lib/currencies";

/** Render either the SVG Syrian flag or the emoji flag. */
const Flag = ({ code, size = 18 }) =>
  code === "SYP"
    ? <SyrianFlag width={size} height={Math.round(size * 0.7)} />
    : <span className="align-middle" style={{ fontSize: size }}>{CURRENCY_MAP[code]?.flag || "🏳️"}</span>;

export default function Currencies() {
  const { currentCompany } = useAuth();
  const base = (currentCompany?.base_currency || "SYP").toUpperCase();

  // rates[X] = value of 1 unit of X in BASE currency (base itself is always 1).
  const [rates, setRates] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [newCode, setNewCode] = useState("");
  const [amount, setAmount] = useState(1);
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState(base);
  const [history, setHistory] = useState([]);
  const [fetching, setFetching] = useState(false);

  const load = async () => {
    try {
      const [r, h] = await Promise.all([
        api.get("/currencies"),
        api.get("/currencies/history", { params: { days: 30 } }).catch(() => ({ data: [] })),
      ]);
      const stored = { ...(r.data.rates || {}) };
      delete stored[base]; // base is implicit (=1)
      // Convert stored (value of 1 X in base) → display (how many X per 1 base)
      const perBase = {};
      for (const k of Object.keys(stored)) { const v = +stored[k]; perBase[k] = v > 0 ? 1 / v : ""; }
      setRates(perBase);
      setUpdatedAt(r.data.updated_at);
      setHistory(h.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); setTo(base); }, [currentCompany?.id]); // eslint-disable-line

  // Fetch today's Syrian-pound market rates (الليرة اليوم) and fill the form for review.
  const fetchLive = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/currencies/live");
      const vSyp = data.rates || {};                        // value of 1 X in SYP
      const baseSyp = base === "SYP" ? 1 : +vSyp[base];      // value of 1 base in SYP
      if (!baseSyp) { toast.error(`سعر عملة الأساس (${base}) غير متوفر من المصدر — أدخله يدوياً`); return; }
      setRates((prev) => {
        const next = { ...prev };
        for (const code of Object.keys(vSyp)) {
          if (code === base) continue;
          const valInBase = +vSyp[code] / baseSyp;           // value of 1 code in base
          if (valInBase > 0) next[code] = 1 / valInBase;     // display = how many code per 1 base
        }
        return next;
      });
      toast.success(`تم جلب ${data.count} سعر من ${data.source} — راجعها ثم اضغط «حفظ الأسعار»`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setFetching(false); }
  };

  // Currencies the user can still add (not base, not already listed)
  const available = useMemo(
    () => CURRENCY_CATALOG.filter((c) => c.code !== base && rates[c.code] === undefined),
    [rates, base]
  );

  const addCurrency = () => {
    if (!newCode) return;
    setRates((r) => ({ ...r, [newCode]: "" }));
    setNewCode("");
  };
  const removeCurrency = (code) => {
    setRates((r) => { const n = { ...r }; delete n[code]; return n; });
  };
  const setRate = (code, val) => {
    setRates((r) => ({ ...r, [code]: val === "" ? "" : val }));
  };

  const save = async () => {
    try {
      const clean = {};
      for (const k of Object.keys(rates)) {
        const v = +rates[k];
        if (v > 0) clean[k] = 1 / v;  // store value of 1 X in base = 1 / (X per base)
      }
      await api.put("/currencies", { rates: clean });
      toast.success("تم حفظ أسعار الصرف يدوياً"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // rates[code] holds "how many units of code per 1 base". Base per-base = 1.
  const perBaseOf = (code) => (code === base ? 1 : (+rates[code] || 0));
  const valueInBase = (code) => (code === base ? 1 : (rates[code] ? 1 / rates[code] : 0));

  // Convert amount from → to: amount_in_base = amt / perBase(from); result = * perBase(to)
  const result = useMemo(() => {
    const amt = +amount || 0;
    const pFrom = perBaseOf(from);
    const pTo = perBaseOf(to);
    if (!pFrom || !pTo) return 0;
    return (amt * pTo) / pFrom;
  }, [amount, from, to, rates, base]); // eslint-disable-line

  const swap = () => { const f = from; setFrom(to); setTo(f); };

  const listedCodes = useMemo(() => [base, ...Object.keys(rates)], [rates, base]);

  return (
    <div className="space-y-6" data-testid="currencies-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold">أسعار الصرف (يدوي)</h1>
        <p className="text-sm text-muted-foreground mt-1">
          عملة الأساس لهذه الشركة هي <b className="text-primary">{currencyName(base)} ({base})</b>. أدخل قيمة كل عملة أخرى مقابل عملة الأساس.
        </p>
        {updatedAt && <p className="text-xs text-muted-foreground mt-1">آخر تحديث: {new Date(updatedAt).toLocaleString("ar-EG")}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border bg-primary/5 border-primary/20 text-primary" data-testid="rate-mode-badge">
          ✍️ وضع يدوي — مع إمكانية جلب أسعار اليوم تلقائياً
        </div>
        <Button variant="outline" size="sm" className="gap-2 h-9" onClick={fetchLive} disabled={fetching} data-testid="fetch-live-rates">
          <RefreshCw className={`w-4 h-4 ${fetching ? "animate-spin" : ""}`} /> جلب أسعار الليرة اليوم
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Rate entry */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-primary" />
            <h2 className="font-heading font-bold">كم تساوي <span className="text-primary">1 {base}</span> من كل عملة؟</h2>
          </div>
          <div className="text-xs text-muted-foreground -mt-1 mb-1">
            مثال: إذا كان 1 {base} = 13,500 ليرة سورية، اكتب <b>13500</b> في حقل SYP
          </div>

          {/* Base row */}
          <div className="grid grid-cols-[1fr,180px] items-center gap-3">
            <div className="font-heading font-bold flex items-center gap-2">
              <Star className="w-4 h-4 text-primary" /><Flag code={base} size={20} /> {currencyName(base)}
              <span className="text-[10px] px-2 py-0.5 rounded bg-primary text-primary-foreground">أساس</span>
            </div>
            <Input value={1} disabled className="tabular-nums text-end font-bold text-lg h-11" />
          </div>

          {listedCodes.filter((c) => c !== base).map((code) => (
            <div key={code} className="grid grid-cols-[1fr,180px,auto] items-center gap-2">
              <div>
                <div className="font-heading font-bold flex items-center gap-2">
                  <Flag code={code} size={20} /> {currencyName(code)}
                </div>
                <div className="text-xs text-muted-foreground font-mono">1 {base} = ؟ {code}</div>
              </div>
              <Input
                type="number" step="any" min="0"
                value={rates[code] === "" || rates[code] === undefined ? "" : rates[code]}
                onChange={(e) => setRate(code, e.target.value)}
                placeholder="غير مُدخل"
                className="tabular-nums text-end font-bold text-lg h-11"
                data-testid={`rate-${code}-input`}
              />
              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => removeCurrency(code)} data-testid={`remove-currency-${code}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          {/* Add currency */}
          <div className="grid grid-cols-[1fr,auto] items-center gap-2 pt-2 border-t border-border">
            <Select value={newCode} onValueChange={setNewCode}>
              <SelectTrigger className="h-11" data-testid="add-currency-select"><SelectValue placeholder="أضف عملة جديدة…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {available.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={16} /> {c.name} ({c.code})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={addCurrency} disabled={!newCode} className="h-11 gap-1" data-testid="add-currency-btn">
              <Plus className="w-4 h-4" /> إضافة
            </Button>
          </div>

          <Button onClick={save} className="h-11 w-full" data-testid="save-rates-button">
            <Save className="w-4 h-4 me-2" /> حفظ الأسعار
          </Button>
        </Card>

        {/* Converter */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="w-4 h-4 text-primary" />
            <h2 className="font-heading font-bold">حاسبة تحويل العملات</h2>
          </div>
          <div className="space-y-3">
            <div>
              <Label>المبلغ</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="converter-amount" className="tabular-nums text-lg font-bold h-11" />
            </div>
            <div className="grid grid-cols-[1fr,auto,1fr] items-end gap-2">
              <div>
                <Label>من</Label>
                <Select value={from} onValueChange={setFrom}>
                  <SelectTrigger data-testid="converter-from" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {listedCodes.map((c) => <SelectItem key={c} value={c}><span className="inline-flex items-center gap-1.5"><Flag code={c} size={16} /> {currencyName(c)} ({c})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="icon" onClick={swap} className="h-11" title="عكس" data-testid="converter-swap">
                <ArrowLeftRight className="w-4 h-4" />
              </Button>
              <div>
                <Label>إلى</Label>
                <Select value={to} onValueChange={setTo}>
                  <SelectTrigger data-testid="converter-to" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {listedCodes.map((c) => <SelectItem key={c} value={c}><span className="inline-flex items-center gap-1.5"><Flag code={c} size={16} /> {currencyName(c)} ({c})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="border border-primary/30 bg-primary/5 rounded-md p-5 text-center" data-testid="converter-result-block">
            <div className="text-xs text-muted-foreground mb-1">الناتج</div>
            <div className="text-3xl font-heading font-bold text-primary tabular-nums" data-testid="converter-result">
              {result.toLocaleString("ar-EG", { maximumFractionDigits: 4 })}
            </div>
            <div className="text-sm text-muted-foreground mt-1">{currencyName(to)} ({to})</div>
            <div className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
              1 {from} = <b>{valueInBase(from).toLocaleString("ar-EG", { maximumFractionDigits: 6 })} {base}</b> · 1 {to} = <b>{valueInBase(to).toLocaleString("ar-EG", { maximumFractionDigits: 6 })} {base}</b>
            </div>
          </div>
        </Card>
      </div>

      {/* Rate history */}
      {history.length > 0 && (
        <Card className="p-5" data-testid="rate-history-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h2 className="font-heading font-bold">تاريخ الأسعار — آخر 30 يوم ({history.length} تحديث)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="p-2 text-start">التاريخ</th>
                  <th className="p-2 text-start">الأساس</th>
                  <th className="p-2 text-start">الأسعار</th>
                  <th className="p-2 text-start">بواسطة</th>
                </tr>
              </thead>
              <tbody>
                {history.slice().reverse().slice(0, 30).map((h) => (
                  <tr key={h.id} className="border-b border-border">
                    <td className="p-2 text-xs">{new Date(h.created_at).toLocaleString("ar-EG", { dateStyle: "short", timeStyle: "short" })}</td>
                    <td className="p-2 font-bold text-primary">{h.base}</td>
                    <td className="p-2 text-xs font-mono">{Object.entries(h.rates || {}).filter(([k]) => k !== h.base).map(([k, v]) => `${k}:${v}`).join(" · ") || "—"}</td>
                    <td className="p-2 text-xs text-muted-foreground">{h.by_name || "يدوي"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
